import { utc } from "@date-fns/utc";
import { format } from "date-fns";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { type AppAccount } from "../../state/api.types";
import { formatAddressWithoutJoining } from "../shared/addressUtils";
import { DetailItem } from "../shared/DetailItem";
import { formatTermMonthOptions } from "./accountPlanHelpers";
import { AccountPlanPreview } from "./AccountPlanPreview";
import { AccountStatus } from "./AccountStatus";

type AccountSlideoutDetailsProps = {
  account: AppAccount;
};

export const AccountSlideoutDetails = (props: AccountSlideoutDetailsProps) => {
  const location = props.account.locations.at(0);
  const preliminaryAddress = props.account.preliminary_address;
  const hasLocationAddress =
    location !== undefined && formatAddressWithoutJoining(location).length > 0;
  const suggestedStartDate =
    props.account.enrollment.requirements?.service_start_date?.value?.value;
  const termMonthOptions = props.account.term_month_options ?? [];

  return (
    <div className="flex flex-col gap-6xl pb-6xl">
      <div className="flex flex-col gap-3xl">
        <div className="text-md font-semibold">Account</div>
        <div>
          <div className="mb-xs text-sm font-medium text-secondary">Status</div>
          <AccountStatus account={props.account} showDescription />
        </div>
        <DetailItem
          label="Account number"
          value={props.account.account_number}
          actions={["copy"]}
        />
        <DetailItem
          label="Email"
          value={props.account.email}
          actions={["email", "copy"]}
        />
        <DetailItem
          label="Phone"
          value={
            props.account.phone_number &&
            parsePhoneNumberFromString(
              props.account.phone_number,
            )?.formatNational()
          }
          actions={["phone", "copy"]}
        />
        <div className="text-sm">
          <div className="mb-xs font-medium text-secondary">Plan</div>
          {location && (
            <AccountPlanPreview location={location} showDescription />
          )}
        </div>
        <DetailItem
          label="UUID"
          value={props.account.uuid}
          actions={["copy"]}
        />
        <DetailItem
          label="External ID"
          value={props.account.external_id}
          actions={["copy"]}
        />
        {!hasLocationAddress && (
          <DetailItem
            label="Preliminary address"
            value={
              preliminaryAddress
                ? formatAddressWithoutJoining(preliminaryAddress).join("\n")
                : undefined
            }
            actions={["copy"]}
            tooltip="An address saved to the account before enrollment finishes, by you or by the customer. It has not been matched to a meter yet, so the confirmed service address can differ."
          />
        )}
        {!location && (
          <>
            <DetailItem
              label="Term month options"
              value={formatTermMonthOptions(termMonthOptions)}
              actions={[]}
              tooltip="Custom contract lengths saved to this account for the customer to choose from. Without any, the plan's own terms apply."
            />
            <DetailItem
              label="Service start date (suggested)"
              value={
                suggestedStartDate
                  ? format(suggestedStartDate, "PPP", { in: utc })
                  : undefined
              }
              actions={[]}
              tooltip="The default service start date saved to this account. The customer can change it in the enrollment flow."
            />
          </>
        )}
      </div>
      {location && (
        <div className="flex flex-col gap-3xl">
          <div className="text-md font-semibold">Location</div>
          <DetailItem
            label="Street address"
            value={formatAddressWithoutJoining(location).join("\n")}
            actions={["copy"]}
          />
          <DetailItem
            label="Utility number"
            value={location.utility_number}
            actions={["copy"]}
          />
          <DetailItem
            label="Service start date"
            value={
              location.service_start_date
                ? format(location.service_start_date, "PPP", { in: utc })
                : undefined
            }
            actions={[]}
          />
          <DetailItem
            label="Contract end date"
            value={
              location.contract_end
                ? format(location.contract_end, "PPP", { in: utc })
                : undefined
            }
            actions={[]}
          />
          {/*
           * Only set once service actually ends, so it stays out of the way for
           * the accounts still being served.
           */}
          {location.final_service_date && (
            <DetailItem
              label="Service end date"
              value={format(location.final_service_date, "PPP", { in: utc })}
              actions={[]}
            />
          )}
          <DetailItem label="UUID" value={location.uuid} actions={["copy"]} />
        </div>
      )}
      <div className="flex flex-col gap-3xl">
        <div className="text-md font-semibold">
          Transmission and distribution utility
        </div>
        <DetailItem
          label="Name"
          value={location?.tdsp_name}
          actions={["copy"]}
        />
        <DetailItem
          label="Outage phone"
          value={location?.tdsp_phone}
          actions={["phone", "copy"]}
        />
        <DetailItem
          label="Outage link"
          value={location?.tdsp_outage_url}
          actions={["link", "copy"]}
        />
      </div>
    </div>
  );
};
